import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Cell, Pie, PieChart, Tooltip, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid } from "recharts";
import { Bug, FolderGit2, Radar, Activity, ArrowUpRight, Sparkles, Shield } from "lucide-react";
import AppShell from "../../components/argus/AppShell";
import { api } from "../../lib/api";
import SeverityChip from "../../components/argus/SeverityChip";
import { Button } from "../../components/ui/button";

const SEV_COLORS = { critical: "#b91c1c", high: "#ea580c", medium: "#ca8a04", low: "#0891b2", info: "#64748b" };

// OWASP 2025 — gold-to-bronze palette, ordered to match A01 → A10
const OWASP_COLORS = ["#c9a35d", "#b88c4a", "#a77637", "#965f24", "#854a14",
                      "#74380a", "#5e2b08", "#492106", "#341805", "#1f1003"];

function KpiCard({ label, value, sub, Icon, testId }) {
  return (
    <div className="rounded-xl border border-line bg-panel p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-ink/50">{label}</span>
        {Icon && <Icon size={16} className="text-ink/40" />}
      </div>
      <div className="font-display text-4xl mt-2 tracking-tight">{value}</div>
      {sub && <div className="text-xs text-ink/50 mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [topAssets, setTopAssets] = useState([]);
  const [activeScans, setActiveScans] = useState([]);
  const [owaspCoverage, setOwaspCoverage] = useState([]);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data));
    api.get("/dashboard/trend").then((r) => setTrend(r.data));
    api.get("/dashboard/top-assets").then((r) => setTopAssets(r.data));
    api.get("/dashboard/owasp-coverage").then((r) => setOwaspCoverage(r.data));
    api.get("/scans").then((r) => setActiveScans(r.data.filter((s) => ["queued", "running"].includes(s.status))));
  }, []);

  const sevData = summary
    ? Object.entries(summary.by_severity).map(([name, value]) => ({ name, value }))
    : [];

  const total = summary?.open_total || 0;

  return (
    <AppShell title="Dashboard" actions={
      <Link to="/app/projects"><Button className="bg-gold text-paper hover:bg-gold2 h-10 font-semibold" data-testid="dashboard-new-scan">Start a Scan</Button></Link>
    }>
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard label="Open Findings" value={total} sub={`${summary?.fixed_total ?? 0} fixed`} Icon={Bug} testId="kpi-findings" />
          <KpiCard label="Projects" value={summary?.projects ?? 0} sub="Pre-loaded demo + your own" Icon={FolderGit2} testId="kpi-projects" />
          <KpiCard label="Active Scans" value={summary?.active_scans ?? 0} sub="Right now" Icon={Radar} testId="kpi-scans" />
          <KpiCard label="MTTR" value={`${summary?.mttr_hours ?? 0}h`} sub="Mean time to remediate" Icon={Activity} testId="kpi-mttr" />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="rounded-xl border border-line bg-panel p-5 lg:col-span-1" data-testid="severity-donut-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl">By severity</h3>
              <Sparkles size={14} className="text-ink/40" />
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={sevData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={2}>
                    {sevData.map((d) => (
                      <Cell key={d.name} fill={SEV_COLORS[d.name] || "#cbd5e1"} stroke="none" />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="mt-2 space-y-1.5">
              {sevData.map((d) => (
                <li key={d.name} className="flex items-center justify-between text-sm">
                  <SeverityChip severity={d.name} />
                  <span className="font-mono">{d.value}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-xl border border-line bg-panel p-5 lg:col-span-2" data-testid="trend-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl">30-day trend</h3>
              <span className="text-xs text-ink/50">New findings per day</span>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend}>
                  <defs>
                    <linearGradient id="cgrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#b91c1c" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#b91c1c" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="hgrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ea580c" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#ea580c" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} stroke="#94a3b8" fontSize={11} />
                  <YAxis stroke="#94a3b8" fontSize={11} />
                  <Tooltip />
                  <Area type="monotone" dataKey="critical" stroke="#b91c1c" fill="url(#cgrad)" />
                  <Area type="monotone" dataKey="high" stroke="#ea580c" fill="url(#hgrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="rounded-xl border border-line bg-panel p-5" data-testid="owasp-coverage-card">
            <div className="flex items-center justify-between mb-1">
              <div>
                <h3 className="font-display text-xl flex items-center gap-2">
                  OWASP Top 10:2025
                  <Shield size={14} className="text-gold" />
                </h3>
                <p className="text-xs text-ink/50 mt-0.5">Open findings by 2025 category. Click any slice to filter.</p>
              </div>
              <Link to="/app/findings" className="text-xs text-gold inline-flex items-center gap-1">View all <ArrowUpRight size={12} /></Link>
            </div>
            {owaspCoverage.length === 0 || owaspCoverage.every((d) => d.count === 0) ? (
              <div className="h-56 flex items-center justify-center text-sm text-ink/50">
                No findings yet — <Link to="/app/projects" className="text-gold ml-1">run a scan</Link>.
              </div>
            ) : (
              <div className="grid sm:grid-cols-[200px_1fr] gap-4 items-start mt-2">
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={owaspCoverage.filter((d) => d.count > 0)}
                        dataKey="count"
                        nameKey="code"
                        innerRadius={48}
                        outerRadius={80}
                        paddingAngle={2}
                        onClick={(p) => nav(`/app/findings?owasp=${encodeURIComponent(p?.code || "")}`)}
                        cursor="pointer"
                      >
                        {owaspCoverage.filter((d) => d.count > 0).map((d, i) => (
                          <Cell key={d.code} fill={OWASP_COLORS[i % OWASP_COLORS.length]} stroke="none" />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v, _n, p) => [v, p?.payload?.code + " — " + p?.payload?.name]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <ul className="space-y-1.5 max-h-56 overflow-y-auto pr-1" data-testid="owasp-coverage-list">
                  {owaspCoverage.map((d, i) => (
                    <li key={d.code}>
                      <Link
                        to={`/app/findings?owasp=${encodeURIComponent(d.code)}`}
                        className={`flex items-center justify-between text-xs gap-2 px-2 py-1.5 rounded hover:bg-raised/50 transition-colors ${d.count === 0 ? "opacity-40" : ""}`}
                        data-testid={`owasp-row-${d.code}`}
                      >
                        <span className="flex items-center gap-2 min-w-0">
                          <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: OWASP_COLORS[i % OWASP_COLORS.length] }} />
                          <span className="font-mono text-ink/70">{d.code}</span>
                          <span className="truncate text-ink/80">{d.name}</span>
                        </span>
                        <span className="font-mono text-ink shrink-0">{d.count}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-line bg-panel p-5" data-testid="top-assets-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl">Top 5 risky assets</h3>
              <Link to="/app/findings" className="text-xs text-gold inline-flex items-center gap-1">View all <ArrowUpRight size={12} /></Link>
            </div>
            {topAssets.length === 0 ? (
              <div className="text-sm text-ink/50 py-12 text-center" data-testid="top-assets-empty">
                No risky assets yet. <Link to="/app/projects" className="text-gold">Start your first scan →</Link>
              </div>
            ) : (
              <ul className="divide-y divide-line">
                {topAssets.map((a) => (
                  <li key={a.asset} className="py-3 flex items-center justify-between text-sm">
                    <span className="truncate font-mono text-xs">{a.asset}</span>
                    <span className="flex items-center gap-2">
                      {a.critical > 0 && <span className="sev-chip sev-critical">{a.critical}</span>}
                      {a.high > 0 && <span className="sev-chip sev-high">{a.high}</span>}
                      <span className="text-ink/50">{a.count} total</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-xl border border-line bg-panel p-5" data-testid="active-scans-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl">Active scans</h3>
              <Link to="/app/scans" className="text-xs text-gold inline-flex items-center gap-1">View all <ArrowUpRight size={12} /></Link>
            </div>
            {activeScans.length === 0 ? (
              <div className="text-sm text-ink/50 py-12 text-center" data-testid="active-scans-empty">
                No active scans. <Link to="/app/projects" className="text-gold">Start one →</Link>
              </div>
            ) : (
              <ul className="divide-y divide-line">
                {activeScans.map((s) => (
                  <li key={s.id} className="py-3 text-sm">
                    <div className="flex justify-between mb-1">
                      <span className="font-mono text-xs">{s.scanner.toUpperCase()} · {s.id.slice(0, 8)}</span>
                      <span className="text-ink/60">{s.progress}%</span>
                    </div>
                    <div className="h-1.5 bg-raised rounded-full overflow-hidden">
                      <div className="h-full bg-gold" style={{ width: `${s.progress}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
